import React, {
  useEffect,
} from 'react';

import { ThemeProvider } from 'styled-components';

import { Box } from '@waglabs/box-next';
import { Button } from '@waglabs/button-next';
import { Typography } from '@waglabs/typography';
import { theme } from '@waglabs/base-theme';

import PasswordResetForm from '../../components/forms/PasswordResetForm/PasswordResetForm';
import Tracker from '../../components/Tracker';
import { FormValues } from '../../components/forms/PasswordResetForm/PasswordResetForm';
import { NavigationWrapper } from '../NavigationPage/NavigationWrapper';
import { Container } from '../../components/Container';
import { PasswordRecoverySuccess } from './components/PasswordRecoverySuccess';
import LoadingBoneModal from '../WagSignUp/components/LoadingBoneOverlay';

import {
  ROUTE_LOGIN,
  WALKER_DOWNLOAD_LOGIN_PORTAL,
} from '../../constants';

import {
  usePostPasswordResetMutation,
  usePostPasswordResetSessionMutation,
} from '../../slices/password';
import { ApiErrorV6 } from '../../utils/buildApiErrorPipeline';

type UserTypeIdentifier = 'w' | 'o';

const isOwner = (userType: UserTypeIdentifier) => userType === 'o';

const isWalker = (userType: UserTypeIdentifier) => userType === 'w';

const isValidUserType = (userType: UserTypeIdentifier) =>
  isOwner(userType) || isWalker(userType);

const getLoginUrl = (userType: UserTypeIdentifier) => {
  if (!isValidUserType(userType)) {
    return ROUTE_LOGIN; // fallback
  }

  const url = isOwner(userType) ? ROUTE_LOGIN : WALKER_DOWNLOAD_LOGIN_PORTAL;
  return url;
};

const getUserTypeFromIdentifier = (identifier: UserTypeIdentifier) => {
  if (isWalker(identifier)) {
    return 'walker';
  }
  return 'owner';
};

export type PasswordRecoveryProps = {
  userType: UserTypeIdentifier;
  userId: string;
  resetToken: string;
};

const PasswordRecovery = ({
  userId,
  resetToken,
  userType: userTypeIdentifier,
}: PasswordRecoveryProps) => {
  const loginUrl = getLoginUrl(userTypeIdentifier);

  const [
    executePostPasswordResetSession,
    {
      isLoading: isPostPasswordResetSessionMutationLoading,
      data: passwordResetSessionData,
      error: passwordResetSessionError,
    },
  ] = usePostPasswordResetSessionMutation();

  const [
    executePostPasswordReset,
    {
      isLoading: isPostPasswordResetMutationLoading,
      isSuccess: isPostPasswordResetMutationSuccess,
      error: passwordResetError,
    }
  ] = usePostPasswordResetMutation();

  const handleSubmit = (values: FormValues) => {
    if (!passwordResetSessionData || !passwordResetSessionData.sessionToken) {
      return;
    }

    executePostPasswordReset({
      password: values.password,
      sessionToken: passwordResetSessionData.sessionToken,
    });
  };

  useEffect(() => {
    executePostPasswordResetSession({
      resetToken,
      userRole: {
        identifier: userId,
        type: getUserTypeFromIdentifier(userTypeIdentifier),
      }
    });
  }, [
    executePostPasswordResetSession,
    resetToken,
    userId,
    userTypeIdentifier,
  ]);

  if (isPostPasswordResetMutationSuccess) {
    return (
      <PasswordRecoverySuccess
        loginUrl={loginUrl}
      />
    )
  }

  const error = (
    (passwordResetSessionError ? (passwordResetSessionError as ApiErrorV6).detail : '')
    || (passwordResetError ? (passwordResetError as ApiErrorV6).detail : '')  
  );

  return (
    <NavigationWrapper
      title="Password Recovery"
    >
      <ThemeProvider
        theme={theme}
      >
        <Container
          maxWidth="600px"
          my={5}
        >
          {isPostPasswordResetSessionMutationLoading && (
            <LoadingBoneModal />
          )}
          <Typography
            variant="h1"
            fontFamily="muliBold"
            textAlign="center"
          >
            Reset Password
          </Typography>

          <Box
            backgroundColor="#efefef"
            p={3}
            borderRadius="8px"
            textAlign="center"
          >
            <PasswordResetForm
              error={error}
              isLoading={isPostPasswordResetMutationLoading}
              onSubmit={handleSubmit}
            />

            <Button
              href={loginUrl}
              my={1}
            >
              Back to login
            </Button>
          </Box>
        </Container>
      </ThemeProvider>
      <Tracker/>
    </NavigationWrapper>
  );
};

export default PasswordRecovery;
